import { Button, ButtonLink } from "design-system/atoms/Button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { FormLabel } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { ensure } from "lopermut"
import { ArrowUpRight, Check } from "lucide-react"
import { WhatsappOpeningMessagePreview } from "modules/campaigns/components/Sections/channels/whatsappOpeningMessages/WhatsappOpeningMessagePreview"
import { useCampaignQuery } from "modules/campaigns/hooks/useCampaignQuery"
import { useWhatsappOpeningMessagesQuery } from "modules/campaigns/hooks/sections/openingMessages/useWhatsappOpeningMessagesQuery"
import { useWhatsappNumbersQuery } from "modules/settings/hooks/whatsapp/useWhatsappNumbersQuery"
import { Loading } from "modules/shared/components/Loading"
import { Pagination } from "modules/shared/components/Pagination"
import { SearchQuery } from "modules/shared/components/SearchQuery"
import { useLanguageLabel } from "modules/shared/hooks/useLanguageLabel"
import { useEffect, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CampaignStatusEnum } from "schema"

type Props = {
  campaignId: string
  isDisabled: boolean
}

const ITEMS_PER_PAGE = 20

export function WhatsappOpeningMessagesPicker({ campaignId, isDisabled }: Props) {
  const { t } = useTranslation()
  const languageLabel = useLanguageLabel()
  const form = useFormContext()
  const { data: campaign } = useCampaignQuery(campaignId)
  const [query, setQuery] = useState("")
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)

  const whatsappNumberId = form.watch("whatsappNumberId" as "name") as string | undefined
  const openingMessages = (form.watch("openingMessages" as "name") ?? []) as Array<{
    id: string
    locale: string
    identifier: string
  }>
  const selectedOpeningMessages = new Set(openingMessages.map((m) => m.id))

  const persistedOpeningMessages = useMemo(
    () => new Set((campaign?.openingMessages ?? []).map((m) => m.id)),
    [campaign?.openingMessages],
  )

  const { data: whatsappOpeningMessages, isPending: isLoadingMessages } =
    useWhatsappOpeningMessagesQuery({
      query: { query, page, total: ITEMS_PER_PAGE },
      onSuccess: (data) => setCount(data.pageInfo.count),
    })

  const { data: whatsappNumbers, isPending: isLoadingNumbers } = useWhatsappNumbersQuery()

  const isLoading = isLoadingMessages || isLoadingNumbers

  function onSelectOpeningMessage(openingMessage: {
    id: string
    locale: string
    identifier: string
  }) {
    if (selectedOpeningMessages.has(openingMessage.id)) {
      form.setValue(
        "openingMessages",
        openingMessages.filter((m) => m.id !== openingMessage.id),
        { shouldDirty: true },
      )
    } else {
      form.setValue("openingMessages", [...openingMessages, openingMessage], {
        shouldDirty: true,
      })
    }
  }

  useEffect(() => {
    if (whatsappNumbers && whatsappNumbers.length && !whatsappNumberId) {
      form.setValue("whatsappNumberId", ensure(whatsappNumbers.at(0)).id, {
        shouldDirty: true,
      })
    }
  }, [whatsappNumbers, whatsappNumberId, form])

  const totalPages = Math.ceil(count / ITEMS_PER_PAGE)

  if (isLoading || !campaign) return <Loading />

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col gap-2 w-full">
        <div>
          <FormLabel>{t("campaigns.channel.whatsapp.number")}</FormLabel>
          <div className="text-muted-foreground text-sm">
            {t("campaigns.channel.whatsapp.numberDescription")}
          </div>
        </div>
        <Select
          value={whatsappNumberId}
          onValueChange={(value) => form.setValue("whatsappNumberId", value, { shouldDirty: true })}
          disabled={isDisabled}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("campaigns.channel.whatsapp.number")} />
          </SelectTrigger>
          <SelectContent>
            {whatsappNumbers?.map((number) => (
              <SelectItem key={number.id} value={number.id}>
                <span>
                  {number.number}
                  {" - "}
                  {number.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-2 w-full">
        <FormLabel>{t("campaigns.channel.whatsapp.openingMessages.title")}</FormLabel>
        <div className="text-muted-foreground text-sm">
          {t("campaigns.channel.whatsapp.openingMessages.description")}
        </div>
      </div>
      <SearchQuery
        placeholder={t("campaigns.channel.whatsapp.openingMessages.searchPlaceholder")}
        setQuery={(newQuery) => {
          setQuery(newQuery)
          setPage(1)
        }}
        defaultValue={query}
      />
      <div className="grid grid-cols-1 gap-2 w-full">
        {whatsappOpeningMessages?.map((openingMessage) => {
          const isSelected = selectedOpeningMessages.has(openingMessage.id)
          const isRowDisabled =
            campaign.status !== CampaignStatusEnum.Draft &&
            persistedOpeningMessages.has(openingMessage.id)

          return (
            <Button
              key={openingMessage.id}
              type="button"
              variant="outline"
              className={cn("w-full h-full bg-card p-0", isSelected && "border-success")}
              onClick={() => onSelectOpeningMessage(openingMessage)}
              disabled={isRowDisabled || isDisabled}
            >
              <div className="flex flex-col gap-2 w-full h-full p-3">
                <div className="flex flex-row gap-2 items-center justify-between w-full">
                  <div className="flex flex-row gap-1">
                    <span className="text-base">{openingMessage.identifier}</span>
                    <span className="text-base text-muted-foreground truncate">
                      {`(${languageLabel(openingMessage.locale)})`}
                    </span>
                  </div>
                  <div className={cn("text-transparent", isSelected && "text-success")}>
                    <Check />
                  </div>
                </div>
                <WhatsappOpeningMessagePreview
                  text={openingMessage.text}
                  identifier={openingMessage.identifier}
                  className="rounded-md gap-1"
                />
              </div>
            </Button>
          )
        })}
        {whatsappOpeningMessages?.length === 0 && (
          <div className="flex flex-col py-8 gap-4 w-full items-center justify-center col-span-2">
            <span className="text-sm text-muted-foreground">
              {t("campaigns.channel.whatsapp.openingMessages.notFound")}
            </span>
            <ButtonLink
              to="/settings/channels/whatsapp/openings/create"
              target="_blank"
              className="flex gap-2"
            >
              {t("campaigns.channel.whatsapp.openingMessages.create")}
              <ArrowUpRight size={16} />
            </ButtonLink>
          </div>
        )}
      </div>
      {whatsappOpeningMessages && whatsappOpeningMessages.length > 0 && (
        <div className="flex flex-col gap-2 w-full max-w-2xl lg:px-4">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            count={count}
          />
        </div>
      )}
    </div>
  )
}
